import * as THREE from 'three';
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import Formatting from "../../Helpers/Formatting";
import { SVGLabel } from "../../Overlays/SVGObjects";
import { SVGOverlay } from "../../Overlays/SVGOverlay";
import { ViewerLayers } from "../../Enums/ViewerLayers";
import { BaseMeasurement, EMeasurementColors, MeasurementData } from "./BaseMeasurement";

export class DistanceMeasurement extends BaseMeasurement {
    _guideMeasurements: Line2;
    _halfPoint1: THREE.Vector3;
    _halfPoint2: THREE.Vector3;

    constructor(formatter: Formatting, startPos: THREE.Vector3, endPos: THREE.Vector3, startNormal: THREE.Vector3, endNormal: THREE.Vector3) {
        super(formatter, startPos, endPos, startNormal, endNormal);

        var dir = endPos.clone().sub(startPos);
        this.value = dir.length();
        dir.normalize();
        let helperLine: THREE.Ray = (startNormal.dot(dir) > 0) ? new THREE.Ray(startPos, startNormal) : new THREE.Ray(startPos, startNormal.clone().negate());
        this._halfPoint1 = new THREE.Vector3();
        helperLine.closestPointToPoint(endPos, this._halfPoint1);


        var iDir = dir.clone().negate();
        let helperLine2: THREE.Ray = (endNormal.dot(iDir) > 0) ? new THREE.Ray(endPos, endNormal) : new THREE.Ray(endPos, endNormal.clone().negate());
        this._halfPoint2 = new THREE.Vector3();
        helperLine2.closestPointToPoint(startPos, this._halfPoint2);

        //Main line
        const geometry = new LineGeometry();
        geometry.setPositions([startPos.x, startPos.y, startPos.z, endPos.x, endPos.y, endPos.z]);
        const matLine = new LineMaterial({
            color: 0x000000,
            linewidth: 0.0025, // in pixels
            dashed: false,
            depthTest: false,

        });
        this._mainMeasurement = new Line2(geometry, matLine);
        this._mainMeasurement.layers.set(ViewerLayers.INFO);

        //Guidelines
        var guideGeometry = new LineGeometry();
        let points: number[] = [];
        let colors: number[] = [];
        const color = new THREE.Color()
        points.push(startPos.x, startPos.y, startPos.z);
        points.push(this._halfPoint1.x, this._halfPoint1.y, this._halfPoint1.z);
        color.set(EMeasurementColors.RED);
        colors.push(color.r, color.g, color.b);
        colors.push(color.r, color.g, color.b);

        points.push(endPos.x, endPos.y, endPos.z);
        points.push(this._halfPoint2.x, this._halfPoint2.y, this._halfPoint2.z);
        color.set(EMeasurementColors.GREEN);
        colors.push(color.r, color.g, color.b);
        colors.push(color.r, color.g, color.b);

        points.push(this._halfPoint1.x, this._halfPoint1.y, this._halfPoint1.z);
        points.push(this._halfPoint2.x, this._halfPoint2.y, this._halfPoint2.z);
        color.set(EMeasurementColors.BLUE);
        colors.push(color.r, color.g, color.b);
        colors.push(color.r, color.g, color.b);

        guideGeometry.setPositions(points);
        guideGeometry.setColors(colors);
        const matGuideLine = new LineMaterial({
            color: 0xffffff,
            linewidth: 0.0025, // in pixels
            vertexColors: true,
            dashed: false,
            depthTest: false
        });
        this._guideMeasurements = new Line2(guideGeometry, matGuideLine);
        this._mainMeasurement.add(this._guideMeasurements);

        this._mainMeasurement.computeLineDistances();
        this._mainMeasurement.scale.set(1, 1, 1);

        this._guideMeasurements.computeLineDistances();
        this._guideMeasurements.scale.set(1, 1, 1);
    }

    updatePoints(startPos: THREE.Vector3, endPos: THREE.Vector3, startNormal: THREE.Vector3, endNormal: THREE.Vector3) {
        this._startPos = startPos;
        this._endPos = endPos;
        this._startNormal = startNormal;
        this._endNormal = endNormal;
        // Check dir
        var dir = endPos.clone().sub(startPos);
        this.value = dir.length();
        dir.normalize();
        let helperLine: THREE.Ray = (startNormal.dot(dir) > 0) ? new THREE.Ray(startPos, startNormal) : new THREE.Ray(startPos, startNormal.clone().negate());
        helperLine.closestPointToPoint(endPos, this._halfPoint1);
        let helperLine2: THREE.Ray = (endNormal.dot(dir) < 0) ? new THREE.Ray(endPos, endNormal) : new THREE.Ray(endPos, endNormal.clone().negate());
        helperLine2.closestPointToPoint(startPos, this._halfPoint2);


        // Main Line
        const geometry = new LineGeometry();
        geometry.setPositions([startPos.x, startPos.y, startPos.z, endPos.x, endPos.y, endPos.z]);
        this._mainMeasurement.geometry = geometry;

        //Guidelines
        var guideGeometry = new LineGeometry();
        let points: number[] = [];
        let colors: number[] = [];
        const color = new THREE.Color();
        if (startPos.distanceToSquared(this._halfPoint1) > 0.1) {
            points.push(startPos.x, startPos.y, startPos.z); // 0
            points.push(this._halfPoint1.x, this._halfPoint1.y, this._halfPoint1.z); // 1
            color.set(EMeasurementColors.RED);
            colors.push(color.r, color.g, color.b);
            colors.push(color.r, color.g, color.b);
        }

        if (this._halfPoint1.distanceToSquared(this._halfPoint2) > 0.1) {
            points.push(this._halfPoint1.x, this._halfPoint1.y, this._halfPoint1.z);
            points.push(this._halfPoint2.x, this._halfPoint2.y, this._halfPoint2.z); // 2
            color.set(EMeasurementColors.GREEN);
            colors.push(color.r, color.g, color.b);
            colors.push(color.r, color.g, color.b);
        }

        if (endPos.distanceToSquared(this._halfPoint2) > 0.1 && startPos.distanceToSquared(this._halfPoint2) > 0.1) {
            points.push(this._halfPoint2.x, this._halfPoint2.y, this._halfPoint2.z);
            points.push(endPos.x, endPos.y, endPos.z); // 3
            color.set(EMeasurementColors.BLUE);
            colors.push(color.r, color.g, color.b);
            colors.push(color.r, color.g, color.b);
        }
        if (points.length > 0) {
            guideGeometry.setPositions(points);
            guideGeometry.setColors(colors);
            this._guideMeasurements.geometry = guideGeometry;
            this._guideMeasurements.computeLineDistances();
        }
        this._mainMeasurement.computeLineDistances();
    }

    addLabels(svgOverlay: SVGOverlay): SVGLabel[] {
        let labels: SVGLabel[] = [];
        let worldPos = this._mainMeasurement.getWorldPosition(new THREE.Vector3());
        let midPos = this._startPos.clone().add(this._endPos).divideScalar(2);
        // Main
        labels.push(svgOverlay.createLabel(12, this.formatter.format(this.value), midPos, { fontColor: 'white', background: 'black', border: 'grey', stroke: 'white' }));
        // X
        let posX = this._startPos.clone().add(this._halfPoint1).divideScalar(2);
        let dist = this._startPos.distanceTo(this._halfPoint1);

        if (dist > 0.1) {
            labels.push(svgOverlay.createLabel(12, "X= " + this.formatter.format(dist), posX.add(worldPos), { fontColor: 'White', background: EMeasurementColors.RED, border: EMeasurementColors.DARK_RED, stroke: 'white' }));
        }
        // Z
        let posZ = this._endPos.clone().add(this._halfPoint2).divideScalar(2).add(this._mainMeasurement.position);;
        dist = this._endPos.distanceTo(this._halfPoint2);
        if (dist > 0.1 && this._startPos.distanceToSquared(this._halfPoint2) > 0.1) {
            labels.push(svgOverlay.createLabel(12, "Z= " + this.formatter.format(dist), posZ.add(worldPos), { fontColor: 'White', background: EMeasurementColors.BLUE, border: EMeasurementColors.DARK_BLUE, stroke: 'white' }));
        }
        // Y
        let posY = this._halfPoint1.clone().add(this._halfPoint2).divideScalar(2).add(this._mainMeasurement.position);;
        dist = this._halfPoint1.distanceTo(this._halfPoint2);
        if (dist > 0.1) {
            labels.push(svgOverlay.createLabel(12, "Y= " + this.formatter.format(dist), posY.add(worldPos), { fontColor: 'White', background: EMeasurementColors.GREEN, border: EMeasurementColors.DARK_GREEN, stroke: 'white' }));
        }


       
        return labels;
    }
    getData(): MeasurementData {
        return {
            measurement:'distance',
            startPos: this._startPos,
            endPos: this._endPos,
            startNormal: this._startNormal,
            endNormal: this._endNormal
        }
    }
}